import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError, onUnauthorized, setCsrfToken } from './api/client';
import type { Company, Me, Page } from './api/types';

interface Session {
  me: Me;
  companyId: string | null;
  company: Company | null;
  companies: { id: string; name: string }[];
  setCompanyId: (id: string) => void;
  can: (permission: string) => boolean;
  logout: () => Promise<void>;
  refresh: () => Promise<unknown>;
}

const Ctx = createContext<Session | null>(null);
// Empresa selecionada: conveniência por navegador (a autorização é sempre verificada no servidor).
const KEY = 'cftv.company';

export function useSession() {
  const s = useContext(Ctx);
  if (!s) throw new Error('Sessão indisponível');
  return s;
}

export function useCompanyId(): string {
  const { companyId } = useSession();
  if (!companyId) throw new Error('Nenhuma empresa selecionada');
  return companyId;
}

export function SessionProvider({ children, fallback }: { children: (s: Session) => ReactNode; fallback: (state: 'loading' | 'anonymous' | 'error', retry: () => void, error?: unknown) => ReactNode }) {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        const r = await api.get<Me>('/api/v1/auth/me', { silent401: true });
        setCsrfToken(r.csrfToken);
        return r;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: 1,
  });

  useEffect(() => onUnauthorized(() => {
    setCsrfToken(null);
    qc.setQueryData(['me'], null);
  }), [qc]);

  // Admin da plataforma pode abrir qualquer empresa; demais usuários, apenas seus vínculos.
  const companies = useQuery({
    queryKey: ['companies-switch', me.data?.user.id],
    enabled: !!me.data?.user.isPlatformAdmin,
    queryFn: () => api.get<Page<{ id: string; name: string }>>('/api/v1/companies?pageSize=100'),
  });
  const list = useMemo(() => {
    if (!me.data) return [];
    if (me.data.user.isPlatformAdmin) return (companies.data?.items ?? []).map((c) => ({ id: c.id, name: c.name }));
    return me.data.memberships.map((m) => ({ id: m.companyId, name: m.companyName }));
  }, [me.data, companies.data]);

  const [companyId, setCompanyIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(KEY);
    } catch {
      return null;
    }
  });
  const effectiveId = list.find((c) => c.id === companyId)?.id ?? list[0]?.id ?? null;
  const setCompanyId = useCallback((id: string) => {
    setCompanyIdState(id);
    try {
      localStorage.setItem(KEY, id);
    } catch {}
    qc.removeQueries({ predicate: (q) => q.queryKey[0] === 'c' });
  }, [qc]);

  const company = useQuery({
    queryKey: ['c', effectiveId, 'company'],
    enabled: !!effectiveId,
    queryFn: () => api.get<Company>(`/api/v1/companies/${effectiveId}`),
  });

  const logout = useCallback(async () => {
    await api.post('/api/v1/auth/logout').catch(() => {});
    setCsrfToken(null);
    qc.clear();
    qc.setQueryData(['me'], null);
  }, [qc]);

  if (me.isLoading) return <>{fallback('loading', () => me.refetch())}</>;
  if (me.isError) return <>{fallback('error', () => me.refetch(), me.error)}</>;
  if (!me.data) return <>{fallback('anonymous', () => me.refetch())}</>;
  if (me.data.user.isPlatformAdmin && companies.isLoading) return <>{fallback('loading', () => companies.refetch())}</>;
  if (effectiveId && company.isLoading) return <>{fallback('loading', () => company.refetch())}</>;

  const perms = new Set(company.data?.access?.permissions ?? []);
  const session: Session = {
    me: me.data,
    companyId: effectiveId,
    company: company.data ?? null,
    companies: list,
    setCompanyId,
    can: (p) => perms.has(p) && (company.data?.access?.allSites || !['company.settings', 'member.read', 'member.manage', 'gateway.manage', 'audit.read', 'site.manage'].includes(p)),
    logout,
    refresh: () => me.refetch(),
  };
  return <Ctx.Provider value={session}>{children(session)}</Ctx.Provider>;
}
